import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { adminBrandAPI } from '../services/api';
import BrandModal from '../components/BrandModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import '../styles/Brands.css';

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});

  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Modals
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'

  const fetchBrands = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm })
      };

      const response = await adminBrandAPI.getAll(params);
      if (response.data.success) {
        setBrands(response.data.data.brands);
        setPagination(response.data.data.pagination);
      }
    } catch (err) {
      setError('Failed to load brands');
      console.error('Fetch brands error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateBrand = () => {
    setSelectedBrand(null);
    setModalMode('create');
    setShowBrandModal(true);
  };

  const handleEditBrand = (brand) => {
    setSelectedBrand(brand);
    setModalMode('edit');
    setShowBrandModal(true);
  };

  const handleDeleteBrand = (brand) => {
    setSelectedBrand(brand);
    setShowDeleteModal(true);
  };

  const handleBrandSaved = () => {
    setShowBrandModal(false);
    fetchBrands();
  };

  const handleBrandDeleted = () => {
    setShowDeleteModal(false);
    fetchBrands();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="brands-page">
      <div className="page-header">
        <div>
          <h1>Brand Management</h1>
          <p>Manage all brands in the system</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateBrand}>
          <FaPlus /> Add Brand
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {loading ? (
        <div className="loading">Loading brands...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Brand Name</th>
                  <th>Description</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.length > 0 ? (
                  brands.map((brand) => (
                    <tr key={brand._id}>
                      <td><strong>{brand.name}</strong></td>
                      <td>{brand.description || '-'}</td>
                      <td>{new Date(brand.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEditBrand(brand)}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDeleteBrand(brand)}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">
                      No brands found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                disabled={!pagination.hasPrevPage}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>

              <span className="pagination-info">
                Page {pagination.currentPage} of {pagination.totalPages}
                ({pagination.totalBrands} total brands)
              </span>

              <button
                className="btn btn-secondary"
                disabled={!pagination.hasNextPage}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showBrandModal && (
        <BrandModal
          mode={modalMode}
          brand={selectedBrand}
          onClose={() => setShowBrandModal(false)}
          onSaved={handleBrandSaved}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmModal
          item={selectedBrand}
          itemType="brand"
          onClose={() => setShowDeleteModal(false)}
          onDeleted={handleBrandDeleted}
          deleteFunction={adminBrandAPI.delete}
        />
      )}
    </div>
  );
};

export default Brands;
